import i18n, { t } from 'i18next';
import type { LDContext } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import { action, computed, makeObservable, observable } from 'mobx';

import { SOCKET_DISCONNECT_MULTISESSION_USER } from '../../config/constants';
import type { Territory } from '../../features/geography/types';
import { getUserLanguage } from '../../i18n/helpers';
import { analytics } from '../../services/analytics';
import { getRequest, postRequest } from '../../services/api';
import { auth, type Profile, type ThirdPartyStatus } from '../../services/auth';
import { logToNewRelic } from '../../services/debug';
import { createLDContext, identifyUser, ldClient, resolveFlag } from '../../services/launchDarkly';
import { ReferralService } from '../../services/referral.service';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { BUCKET_PREFIX, ENVIRONMENT } from '../../utils/environment';
import { getErrorMessage } from '../../utils/errors';
import { isCmsPages } from '../../utils/isExemptPage';
import { devError } from '../../utils/log';
import { type RootStore } from '../RootStore';

import { LandingKey } from './types';

const LAST_LOGIN_TAB_ID = 'lastLoginTabId';

const DEFAULT_DIRECT_EM_BANNER = 'direct_em_150_offer';

type PendingActionKey =
  | 'signUp'
  | 'thirdPartyAuth'
  | 'signIn'
  | 'resendEmailVerification'
  | 'sendPhoneVerification'
  | 'completePhoneVerification'
  | 'sendPasswordResetLink'
  | 'chooseCountry';

type AuthStatus =
  | 'authenticated'
  | 'thirdPartyFirstLogin'
  | 'unauthenticated'
  | 'userInactive'
  | 'userSelfExcluded'
  | 'authError'
  | 'error';

export class AuthStore {
  private readonly rootStore: RootStore;

  // Possible auth flow scenarios:
  // a) User signs up with credentials. Callback is NEVER triggered since the email is not verified by
  //    the time firebase user arrives. `setUserDataReady(true)` is called within `signInToBackendAndAuthorize()`
  //    when email is verified, `setAuthStatus("authenticated")` is called when the SignUpWizard is closed.
  // b) User goes through SignUpWizard or LogInWizard with third-party auth. Callback is triggered with a non-null
  //    `user` and `thirdPartyStatus` being either "firstLogin" or "nonFirstLogin". `setUserDataReady(true)` gets called
  //    immediately, `authStatus` first becomes "thirdPartyFirstLogin" and then, when WelcomeBonusDialog (and
  //    SignUpPrivacy, if the user went via LogInWizard) dialogs are closed, changes to "authenticated".
  // c) User enters already being signed in, this triggers with non-null `user` and `thirdPartyStatus` being either
  //    "notFirstParty" or "nonFirstLogin".
  // d) User logs in with credentials. Callback is triggered with non-null `user` and
  //    `thirdPartyStatus` = "notThirdParty", user gets authorized immediately.
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    this.setLastLoginTabId();

    if (!isCmsPages()) {
      void auth.subscribeToAuthStateChange(async (user, thirdPartyStatus) => {
        this.isDisconnectedByMultisession = false;
        this.setUserDataReady(!!user);

        if (auth.isUserInactive) {
          this.setAuthStatus('userInactive');
        } else if (auth.isUserSelfExcluded) {
          this.setAuthStatus('userSelfExcluded');
        } else if (auth.isAuthError) {
          this.setAuthStatus('authError');
        } else if (user && thirdPartyStatus.status) {
          this.setAuthStatus(AuthStore.getAuthStatus(thirdPartyStatus.status));
        } else if (!user) {
          this.setAuthStatus('unauthenticated');
        }

        this.resyncUserCooldown();

        let ldContext: LDContext = {
          kind: 'user',
        };

        if (user) {
          void analytics.identify(user.firebaseUid);
          this.setFlowData(thirdPartyStatus.data);
          this.setNewRelicUserAttributes(user);
          this.setupUserEnvironment(user);

          ldContext = createLDContext(user);

          // Handle socket connection based on multi-session prevention and tab ID
          if (!socket.connected) {
            this.handleSocketConnection();
          }
        } else {
          void analytics.logout();
          ldContext = createLDContext(null, this.rootStore.liveGeoStore.currentGeo);
          this.setupGuestEnvironment();
          this.rootStore.gameStore.sendSlotsGameUiMessage({ type: 'logout' });
          socket.disconnect();
        }

        this.setupGeneralEnvironment();

        if (ldClient) {
          try {
            const timeoutSeconds = 1;

            const result = await Promise.race([
              identifyUser(ldContext),
              new Promise((resolve) => setTimeout(() => resolve('timeout'), timeoutSeconds * 1000)),
            ]);

            if (result === 'timeout') {
              void console.warn(`IdentifyUser did not return a result in ${timeoutSeconds} seconds`);
            }
          } catch (error) {
            const errorMessage = getErrorMessage(error);
            console.error(`IdentifyUser failed: ${errorMessage}`);
            // optional: log the error if needed
          }
        }

        if (!this.isAuthenticationFlowDone) {
          this.setIsAuthenticationFlowDone(true);
        }
      });

      socket.on(SOCKET_DISCONNECT_MULTISESSION_USER, () => {
        this.handleMultiSessionDisconnect();
      });
    }
  }

  private handleSocketConnection() {
    const preventMultisessionFlag = resolveFlag('prevent_multisession_enable', false);
    if (preventMultisessionFlag) {
      const lastLoginTabId = localStorage.getItem(LAST_LOGIN_TAB_ID);
      if (lastLoginTabId === this.tabId) {
        socket.connect();
      } else {
        this.handleMultiSessionDisconnect();
      }
    } else {
      socket.connect();
    }
  }

  @action
  setLastLoginTabId() {
    localStorage.setItem(LAST_LOGIN_TAB_ID, this.tabId);
  }

  @action
  private handleMultiSessionDisconnect() {
    this.isDisconnectedByMultisession = true;
    socket.disconnect();
  }

  @computed
  get isSigningUp(): boolean {
    return this.pending('signUp') || this.pending('thirdPartyAuth');
  }

  private setNewRelicUserAttributes(user: Profile) {
    if (window.newrelic) {
      window.newrelic.setUserId(user.firebaseUid);
      window.newrelic.setCustomAttribute('firebaseUid', user.firebaseUid);
      if (user.userWallet.ownerId) {
        window.newrelic.setCustomAttribute('userId', user.userWallet.ownerId);
      }
    }
  }

  private prevLang = getUserLanguage();

  private setupGeneralEnvironment() {
    void this.rootStore.categoriesStore.fetchCategories();

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        void this.rootStore.categoriesStore.updateGameLabelConfigs(lang);
      }
    });

    void this.rootStore.cmsStore.fetchFooter();

    if (!this.rootStore.rewardsStore.welcomeBonus) {
      void this.rootStore.rewardsStore.fetchWelcomeBonus();
    }
  }

  private setupUserEnvironment(profile: Profile) {
    void this.rootStore.userStore.setUserProfile(profile);
    void this.rootStore.rewardsStore.fetchInviteBonusNotification();
    void this.rootStore.gameStore.sendCustomTokenToSlotsGameUi();
    this.rootStore.walletStore.updateWallet(profile.userWallet);
  }

  private setupGuestEnvironment() {
    this.rootStore.rewardsStore.resetDailyBonus();
    this.rootStore.coinTypeStore.setCoinType('GC');
    this.rootStore.walletStore.resetWallet();
    this.rootStore.coinBackStore.resetCoinBack();
    this.rootStore.onboardingStore.setFinishedFtue(false);
    this.rootStore.puzzleStore.resetEvent();
    this.rootStore.hammersStore.resetHammers();
    this.rootStore.trailStore.resetEvent();
    this.rootStore.bingoStore.resetAll();
    this.rootStore.subscriptionStore.resetAll();

    ReferralService.updateReferralData();

    if (!this.isUserExcluded) {
      this.rootStore.userStore.resetUserProfile();
    }
  }

  private static getAuthStatus(thirdPartyStatus: ThirdPartyStatus['status']): AuthStatus {
    switch (thirdPartyStatus) {
      case 'firstLogin':
        return 'thirdPartyFirstLogin';
      case 'notThirdParty':
      case 'nonFirstLogin':
        return 'authenticated';
      default:
        return 'error';
    }
  }

  @observable
  public phoneVerificationFlowData: { countryCode?: string; phoneNumber: string } | null = null;

  @action
  public setPhoneVerificationFlowData(data: { countryCode?: string; phoneNumber: string } | null) {
    this.phoneVerificationFlowData = data;
  }

  @observable
  public sendVerificationEmailCooldown = 0;

  @observable
  private emailCooldownInterval: NodeJS.Timeout | null = null;

  @observable
  public tabId: string = Math.random().toString(36).slice(2, 9); // Random 7-character string

  @observable
  public currentUserEmail: string | null | undefined = undefined;

  @observable
  public userDataReady = Boolean(auth.userId);

  @observable
  public isAuthenticationFlowDone = false;

  @observable
  public isDisconnectedByMultisession = false;

  @observable
  public authStatus: AuthStatus = 'unauthenticated';

  @observable
  public isAuthWizardOpen = false;

  @observable
  public userCoolDown = auth.userCoolDown;

  @observable
  public flowData?: unknown = undefined;

  @observable
  public directRegEmailVerificationStep = false;

  @observable
  public directRegPostVerificationWizard = false;

  @action
  public setDirectRegEmailVerificationStep(step: boolean) {
    this.directRegEmailVerificationStep = step;
  }

  @action
  public setDirectRegPostVerificationWizard(show: boolean) {
    this.directRegPostVerificationWizard = show;
  }

  @action
  public setIsAuthWizardOpen(isOpen: boolean) {
    this.isAuthWizardOpen = isOpen;
  }

  @action
  public setAuthStatus(authStatus: AuthStatus) {
    this.authStatus = authStatus;
  }

  @computed
  public get authorized(): boolean {
    return this.userDataReady && this.authStatus === 'authenticated';
  }

  @computed
  public get isUserExcluded(): boolean {
    return this.authStatus === 'userSelfExcluded' || this.authStatus === 'userInactive';
  }

  @computed
  public get isThirdPartyAuthorizing(): boolean {
    return this.pending('thirdPartyAuth');
  }

  @observable
  public refreshTrigger?: symbol = undefined;

  @observable
  private _pending: Record<PendingActionKey, boolean> = {
    signUp: false,
    thirdPartyAuth: false,
    signIn: false,
    resendEmailVerification: false,
    sendPhoneVerification: false,
    completePhoneVerification: false,
    sendPasswordResetLink: false,
    chooseCountry: false,
  };

  private phoneVerificationId: string | undefined = undefined;

  @observable
  public currentUserPhone: string | undefined = undefined;
  @observable
  public kycAccessToken: string | null = null;

  public pending(actionKey: PendingActionKey) {
    return this._pending[actionKey];
  }

  @action
  public setSendVerificationEmailCooldown(cooldown: number) {
    this.sendVerificationEmailCooldown = cooldown;
  }

  @action
  private setUserDataReady(userDataReady: boolean) {
    this.userDataReady = userDataReady;
  }

  @action
  public setIsAuthenticationFlowDone(newStatus: boolean) {
    this.isAuthenticationFlowDone = newStatus;
  }

  @action
  public setFlowData(data?: unknown) {
    this.flowData = data;
  }

  @action
  public setCurrentUserEmail(email?: string | null) {
    this.currentUserEmail = email;
  }

  @action
  public setCurrentUserPhone(phone?: string) {
    this.currentUserPhone = phone;
  }

  @action
  public setPending(actionKey: PendingActionKey, pending: boolean) {
    this._pending[actionKey] = pending;
  }

  public getLandingKey(): string | null {
    const landingSearchParam = new URLSearchParams(window.location.search).get('landing');
    return landingSearchParam || null;
  }

  public getUtmPromo(): string | null {
    const utmPromo = new URLSearchParams(window.location.search).get('utm_promo');
    return utmPromo || null;
  }

  public isDirectLanding(): boolean {
    const landingKey = this.getLandingKey();
    return (
      !!landingKey &&
      (landingKey.startsWith(LandingKey.DIRECT_SU) ||
        landingKey.startsWith(LandingKey.DIRECT_EM) ||
        landingKey.startsWith(LandingKey.DIRECT_REG))
    );
  }

  public isDirectSignUpLanding(): boolean {
    return this.getLandingKey() === LandingKey.DIRECT_SU || this.isDirectSignUpPrefixLanding();
  }

  public isDirectEmailLanding(): boolean {
    return this.getLandingKey() === LandingKey.DIRECT_EM;
  }

  public isDirectRegLanding(): boolean {
    return this.getLandingKey() === LandingKey.DIRECT_REG;
  }

  public isDirectSignUpPrefixLanding(): boolean {
    const landingKey = this.getLandingKey() || '';
    return landingKey.startsWith(LandingKey.DIRECT_SU) && landingKey !== LandingKey.DIRECT_SU;
  }

  public getDirectSignUpLanderBanner(): string | null {
    if (this.isDirectSignUpPrefixLanding()) {
      const landingKey = this.getLandingKey();
      if (!landingKey) {
        return null;
      }

      const url = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/landing/${landingKey}.webp`;
      return url;
    }

    if (this.isDirectLanding()) {
      const utmPromo = this.getUtmPromo();
      if (!utmPromo) {
        if (this.isDirectEmailLanding()) {
          // default image for direct email landing - 150% offer
          const url = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/landing/${DEFAULT_DIRECT_EM_BANNER}.webp`;
          return url;
        }

        return null;
      }

      const url = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/landing/${utmPromo}.webp`;
      return url;
    }

    return null;
  }

  // sending "updateSearchParams" from outside is necessary to update "useQueryParams" hook listeners
  public clearLandingKey(updateSearchParams: (params: URLSearchParams) => void) {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('landing')) {
      queryParams.delete('landing');
      updateSearchParams(queryParams);
    }
  }

  public setAuthCompleted() {
    this.setAuthStatus('authenticated');
  }

  public async chooseCountry(data: Pick<Profile, 'language'> & Pick<Territory, 'countryCode'>) {
    try {
      this.setPending('chooseCountry', true);
      const result = await this.updateUserProfile(data);
      const { territory, language } = result.data.data ?? {};

      if (!result.errors?.length) {
        this.rootStore.userStore.updateProfile({
          isCountrySetByUser: true,
          territory,
          language,
        });
      }

      return result;
    } finally {
      this.setPending('chooseCountry', false);
    }
  }

  public async updateUserProfile(profile: Partial<Profile>) {
    return await postRequest<ApiResponse<Profile>>('user/profile', profile);
  }

  public async authWithThirdPartyPlatform(platform: 'apple' | 'google' | 'facebook') {
    this.setPending('thirdPartyAuth', true);
    setTimeout(() => {
      this.setPending('thirdPartyAuth', false);
    }, 2000);

    try {
      return await auth.authWithThirdPartyPlatform(platform);
    } finally {
      this.setPending('thirdPartyAuth', false);
    }
  }

  public async signInWithEmail(data: { email: string; password: string }) {
    this.setPending('signIn', true);
    this.setIsAuthenticationFlowDone(false);

    try {
      const user = await auth.signInWithEmail(data);
      this.setCurrentUserEmail(data.email);

      return user;
      /* If logIn errors out, hide the Pending overlay immediately and re-throw the error so that it can be displayed in
       the UI. Otherwise, the overlay will only be hidden in the `auth.subscribeToAuthStateChange` callback, after
       all the necessary on-auth checks and API calls are performed.
    */
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      this.setPending('signIn', false);
    }
  }

  public clearSendVerificationEmailCooldown() {
    if (this.emailCooldownInterval) {
      clearInterval(this.emailCooldownInterval);
      this.emailCooldownInterval = null;
    }

    this.setSendVerificationEmailCooldown(0);
  }

  private async sendVerificationEmail() {
    this.clearSendVerificationEmailCooldown();

    this.setSendVerificationEmailCooldown(60);

    const response = await auth.sendEmailVerification();

    this.emailCooldownInterval = setInterval(() => {
      if (this.sendVerificationEmailCooldown === 0 && this.emailCooldownInterval) {
        this.clearSendVerificationEmailCooldown();
      } else if (this.sendVerificationEmailCooldown > 0) {
        this.setSendVerificationEmailCooldown(this.sendVerificationEmailCooldown - 1);
      }
    }, 1000);

    return response;
  }

  public async signUpWithEmail(credentials: { email: string; password: string }) {
    this.setPending('signUp', true);

    try {
      const user = await auth.signUpWithEmail(credentials);
      this.setCurrentUserEmail(credentials.email);

      if (!user.emailVerified) {
        await this.sendVerificationEmail();
      }

      return user;
    } finally {
      this.setPending('signUp', false);
    }
  }

  public async resendEmailVerification() {
    this.setPending('resendEmailVerification', true);
    try {
      return await this.sendVerificationEmail();
    } finally {
      this.setPending('resendEmailVerification', false);
    }
  }

  public trackEmailVerificationCompletion(callback?: VoidFunction) {
    return auth.trackVerificationCompletion(async () => {
      await this.handleVerificationCompletion();
      if (typeof callback === 'function') {
        callback();
      }
    });
  }

  public async sendPasswordResetLink(email: string) {
    this.setPending('sendPasswordResetLink', true);

    try {
      return await auth.sendPasswordResetLink(email);
    } finally {
      this.setPending('sendPasswordResetLink', false);
    }
  }

  @action
  public resyncUserCooldown() {
    this.userCoolDown = auth.userCoolDown;
  }

  public async handleVerificationCompletion() {
    let profile;

    try {
      profile = await auth.apiLogin();

      if (profile) {
        auth.sendRegisterSuccessEvent('email', profile.email, profile);
      }
    } catch (error) {
      const err = error as Error;

      void logToNewRelic('Failed to handle verification completion', 'Error in handleVerificationCompletion', 'error', {
        error: {
          message: err.message,
        },
      });

      this.setUserDataReady(false);
    } finally {
      if (profile) {
        this.setUserDataReady(true);
        this.setupUserEnvironment(profile);

        if (!socket.connected) {
          socket.connect();
        }
      }
      if (auth.isAuthError) {
        this.setAuthStatus('authError');
      }

      if (auth.isUserInactive) {
        this.setAuthStatus('userInactive');
      }

      if (auth.isUserSelfExcluded) {
        this.setAuthStatus('userSelfExcluded');
      }
    }
  }

  public async sendPhoneVerificationCode(
    phone: string,
  ): Promise<{ exceededRegistrationLimit?: boolean; verified?: boolean }> {
    this.setPending('sendPhoneVerification', true);
    const appVerifier = auth.getRecaptchaVerifier();

    try {
      const { isInUse, verified, exceededRegistrationLimit } = await auth.checkIfPhoneVerified(phone);

      if (exceededRegistrationLimit) {
        return { exceededRegistrationLimit };
      }

      if (isInUse) {
        throw new Error(t('phoneVerification.phoneNumberAlreadyInUseError'));
      }

      if (verified) {
        return { verified };
      }

      this.phoneVerificationId = await auth.sendPhoneVerificationCode(phone, appVerifier);
      this.setCurrentUserPhone(phone);
      return {};
    } finally {
      this.setPending('sendPhoneVerification', false);
      try {
        appVerifier.clear();
      } catch {}
    }
  }

  public async completePhoneVerification(verificationCode: string) {
    if (!this.phoneVerificationId) {
      throw new Error('Phone verification process has not been started yet.');
    }

    this.setPending('completePhoneVerification', true);

    try {
      const verificationResult = await auth.verifyPhone(
        this.phoneVerificationId,
        verificationCode,
        this.phoneVerificationFlowData?.countryCode ?? undefined,
      );

      const currentCountry = this.rootStore.userStore.profile?.cleanGeoData?.countryCode;

      if (currentCountry !== verificationResult.newGeo.countryCode) {
        try {
          // We need to await this so that by the time `openOrder` is called a new list is already there
          await this.rootStore.appStore.fetchStorePackages();
        } catch (packagesErr) {
          devError('Failed to refresh packages on country change', packagesErr);
        }
      }

      if (this.currentUserPhone) {
        const profileUpdates: Partial<Profile> = {
          phone: this.currentUserPhone,
          phoneVerified: true,
          cleanGeoData: verificationResult.newGeo,
        };

        this.rootStore.userStore.updateProfile(profileUpdates);
      } else {
        devError('User phone number does not exist');
      }

      return verificationResult.userCredential;
    } finally {
      this.setPending('completePhoneVerification', false);
    }
  }

  @action
  public setKycAccessToken(token: string | null) {
    this.kycAccessToken = token;
  }

  public async fetchPlayerKycAccessToken(onFailure?: (stores: RootStore, apiError?: unknown) => void): Promise<string> {
    try {
      const response = await getRequest('user/init-access-token');

      const token = get(response, 'data.token', null);
      this.setKycAccessToken(token || null);

      if (!token && typeof onFailure === 'function') {
        onFailure(this.rootStore);
      }

      return token || '';
    } catch (apiError) {
      if (typeof onFailure === 'function') {
        onFailure(this.rootStore, apiError);
      }

      this.setKycAccessToken(null);
      return '';
    }
  }

  public async getKycAccessToken() {
    try {
      const response = await getRequest('user/init-access-token');

      const token = get(response, 'data.token', null);

      if (token) {
        this.setKycAccessToken(token);
      } else {
        this.rootStore.payRedeemStore.setIsRedeemKYCFailed(true);
      }
    } catch (error) {
      this.rootStore.payRedeemStore.setIsRedeemKYCFailed(true);
    }
  }
}
